import React from "react";
import {
  Box,
  Divider,
  Dialog as MuiDialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CloseOutlined,
  DownloadOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { formatFileSize } from "../../../helpers";
import {
  IParsedMsg,
  IParsedMsgAttachment,
  parseMsgArrayBuffer,
} from "../utils/parseMsgAttachment";

export interface IMsgPreviewDialogProps {
  open: boolean;
  title: string;
  /** The .msg file content as an ArrayBuffer. */
  arrayBuffer: ArrayBuffer | null;
  onClose: () => void;
}

interface IInnerPreview {
  name: string;
  url: string;
  contentType: string;
}

export const MsgPreviewDialog: React.FC<IMsgPreviewDialogProps> = ({
  open,
  title,
  arrayBuffer,
  onClose,
}) => {
  const [nestedMsg, setNestedMsg] = React.useState<{
    title: string;
    buffer: ArrayBuffer;
  } | null>(null);
  const [innerPreview, setInnerPreview] = React.useState<IInnerPreview | null>(
    null,
  );

  const parsedMsg: IParsedMsg | null = React.useMemo(() => {
    if (!arrayBuffer) {
      return null;
    }
    return parseMsgArrayBuffer(arrayBuffer);
  }, [arrayBuffer]);

  React.useEffect(() => {
    return () => {
      parsedMsg?.attachments.forEach((attachment) => {
        if (attachment.url && attachment.url !== "#") {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, [parsedMsg]);

  const handlePreviewInner = (attachment: IParsedMsgAttachment) => {
    if (!attachment.contentBytes) {
      return;
    }

    if (attachment.isMsg) {
      try {
        const binary = window.atob(attachment.contentBytes);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }
        setNestedMsg({ title: attachment.name, buffer: bytes.buffer });
      } catch {
        /* ignore parse errors */
      }
      return;
    }

    setInnerPreview({
      name: attachment.name,
      url: attachment.url,
      contentType: attachment.contentType,
    });
  };

  const handleDownloadInner = (attachment: IParsedMsgAttachment) => {
    if (!attachment.url || attachment.url === "#") {
      return;
    }
    const anchor = document.createElement("a");
    anchor.href = attachment.url;
    anchor.download = attachment.name || "attachment";
    anchor.click();
  };

  const renderInnerPreviewBody = () => {
    if (!innerPreview) {
      return null;
    }

    if (innerPreview.contentType.startsWith("image/")) {
      return (
        <Box
          component="img"
          src={innerPreview.url}
          alt={innerPreview.name}
          sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
        />
      );
    }

    if (innerPreview.contentType.includes("pdf")) {
      return (
        <iframe
          src={innerPreview.url}
          style={{ width: "100%", height: "70vh", border: "none" }}
          title={innerPreview.name}
        />
      );
    }

    return (
      <Typography variant="body1" color="text.secondary">
        Preview not available for this file type. Use the download button
        instead.
      </Typography>
    );
  };

  const renderAttachments = (attachments: IParsedMsgAttachment[]) => {
    if (attachments.length === 0) {
      return null;
    }

    return (
      <Box>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
          Attachments ({attachments.length})
        </Typography>

        <Stack spacing={1}>
          {attachments.map((attachment, index) => (
            <Box
              key={`${attachment.name}-${index}`}
              sx={{
                border: "1px solid #dee3e9",
                borderRadius: "8px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                backgroundColor: "#fff",
              }}
            >
              <Box minWidth={0}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  title={attachment.name}
                >
                  {attachment.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(attachment.size)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Preview">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePreviewInner(attachment)}
                      disabled={!attachment.contentBytes}
                    >
                      <VisibilityOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Download">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleDownloadInner(attachment)}
                      disabled={!attachment.contentBytes}
                    >
                      <DownloadOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderMsgBody = () => {
    if (!parsedMsg) {
      return (
        <Typography variant="body1" color="text.secondary">
          Unable to read this Outlook message.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="body2">
            <strong>From:</strong> {parsedMsg.senderName}
            {parsedMsg.senderEmail ? ` (${parsedMsg.senderEmail})` : ""}
          </Typography>
          {parsedMsg.recipients.length > 0 && (
            <Typography variant="body2">
              <strong>To:</strong>{" "}
              {parsedMsg.recipients
                .map((recipient) =>
                  recipient.email
                    ? `${recipient.name ?? ""} (${recipient.email})`
                    : recipient.name ?? "",
                )
                .join(", ")}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Subject:</strong> {parsedMsg.subject}
          </Typography>
        </Box>

        <Divider />

        {parsedMsg.bodyHTML ? (
          <iframe
            srcDoc={parsedMsg.bodyHTML}
            style={{ width: "100%", height: "420px", border: "none" }}
            title={`${title}-body`}
            sandbox=""
          />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {parsedMsg.body || "No message content."}
          </Typography>
        )}

        {parsedMsg.attachments.length > 0 && <Divider />}
        {renderAttachments(parsedMsg.attachments)}
      </Stack>
    );
  };

  return (
    <>
      <MuiDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Typography variant="h6" noWrap title={title}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderMsgBody()}</DialogContent>
      </MuiDialog>

      {/* Nested .msg attachment */}
      {nestedMsg && (
        <MsgPreviewDialog
          open={Boolean(nestedMsg)}
          title={nestedMsg.title}
          arrayBuffer={nestedMsg.buffer}
          onClose={() => setNestedMsg(null)}
        />
      )}

      {/* Inner image / pdf attachment */}
      {innerPreview && (
        <MuiDialog
          open={Boolean(innerPreview)}
          onClose={() => setInnerPreview(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
            }}
          >
            <Typography variant="h6" noWrap title={innerPreview.name}>
              {innerPreview.name}
            </Typography>
            <IconButton onClick={() => setInnerPreview(null)} size="small">
              <CloseOutlined />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{ display: "flex", justifyContent: "center" }}
          >
            {renderInnerPreviewBody()}
          </DialogContent>
        </MuiDialog>
      )}
    </>
  );
};
